import React, { useEffect, useState } from 'react';
import { marketApi, watchlistApi, aiApi } from './api';
import StockLink from './StockLink';

const REFRESH_MS = 15000;

export default function Watchlist({ onSelect, selected }) {
  const [symbols, setSymbols] = useState(null);
  const [newTicker, setNewTicker] = useState('');
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [notes, setNotes] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const addInputRef = React.useRef(null);

  useEffect(() => {
    watchlistApi
      .list()
      .then(setSymbols)
      .catch(() => setSymbols([]));
  }, []);

  useEffect(() => {
    if (!symbols?.length) {
      setQuotes([]);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const data = await marketApi.quotes(symbols);
        if (!cancelled) {
          setQuotes(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };

    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [symbols]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTicker.trim()) return;
    setActionError(null);
    try {
      const updated = await watchlistApi.add(newTicker.trim());
      setSymbols(updated);
      setNewTicker('');
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleRemove = async (symbol, e) => {
    e.stopPropagation();
    try {
      const updated = await watchlistApi.remove(symbol);
      setSymbols(updated);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleScan = async () => {
    if (!symbols?.length) return;
    setScanning(true);
    setScanError(null);
    try {
      const result = await aiApi.watchlistScan(symbols);
      setNotes(result.notes || {});
    } catch (err) {
      setScanError(err.message);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <form className="watchlist-add" onSubmit={handleAdd}>
        <input
          ref={addInputRef}
          type="text"
          placeholder="Add ticker (e.g. SPY)"
          value={newTicker}
          onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
          maxLength={10}
        />
        <button type="submit" className="secondary-button">
          Add
        </button>
      </form>

      {symbols?.length > 0 && (
        <button type="button" className="secondary-button" style={{ marginBottom: 12, width: '100%' }} onClick={handleScan} disabled={scanning}>
          {scanning ? 'Scanning…' : '✦ AI scan watchlist'}
        </button>
      )}

      {actionError && <div className="error-box">{actionError}</div>}
      {error && <div className="error-box">Live quotes unavailable: {error}</div>}
      {scanError && <div className="error-box">{scanError}</div>}

      {symbols?.length === 0 && (
        <div className="empty-state">
          <p>Build your watchlist</p>
          <small>Track stocks, ETFs, and market names you care about.</small>
          <button type="button" className="secondary-button" style={{ marginTop: 12 }} onClick={() => addInputRef.current?.focus()}>
            Add symbols
          </button>
        </div>
      )}

      <div className="watchlist">
        {(quotes === null ? (symbols || []).map((s) => ({ symbol: s, skeleton: true })) : quotes).map((q) => (
          <div key={q.symbol}>
            <div
              className={`watchlist-row ${selected === q.symbol ? 'selected' : ''} ${q.skeleton ? 'disabled' : ''}`}
              onClick={() => !q.skeleton && onSelect && onSelect(q)}
            >
              <StockLink symbol={q.symbol} className="wl-symbol" />
              {q.skeleton ? (
                <span className="skeleton-bar" />
              ) : q.error ? (
                <span className="wl-error">unavailable</span>
              ) : (
                <>
                  <span className="wl-price">${q.price?.toFixed(2)}</span>
                  <span className={`wl-change ${q.change >= 0 ? 'up' : 'down'}`}>
                    {q.change >= 0 ? '▲' : '▼'} {Math.abs(q.changePercent || 0).toFixed(2)}%
                  </span>
                </>
              )}
              <span className="wl-remove" onClick={(e) => handleRemove(q.symbol, e)} title="Remove">
                ×
              </span>
            </div>
            {notes[q.symbol] && <p className="wl-note">✦ {notes[q.symbol]}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
